import { useEffect, useRef, useState, type FormEvent } from 'react';
import { appoppsettTittel, Kodefelt, Oppsettshelt, useAppoppsett, Visningsknapp } from '../auth/Appoppsett.tsx';
import { useAuth } from '../auth/AuthProvider.tsx';
import { useEnhet } from '../auth/enhet.ts';
import { AuthFeil, type Faktorer } from '../auth/typer.ts';
import { Merkehode } from '../skall/Merke.tsx';
import { useSidetittel } from '../skall/tittel.ts';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';

/* ------------------------------------------------------------------------------
   INNSPEL — TOFAKTOR. SPESIFIKASJONEN FOR SKJERMENE FORAN LÅSEN (R6.2).

   Ingen wireframe finnes for innloggingen; denne kommentaren ER spesifikasjonen
   (r0-plan.md, R6.2). Utseendet eies av
   docs/forslag/redesign/konsept/tofaktor-konsept-v0.1-2026-09-20.html, godkjent av
   produkteier 2026-09-20 med P1–P4; de nitten avvikene som ble rettet står i
   tofaktor-avvik-v0.1-2026-09-20.md. DD-71: konseptsiden er forbildet, denne filen
   kravlisten. Testene er test/r62-tofaktor.test.tsx (formen) og test/tofaktor.test.tsx
   (oppførselen mot porten), og e2e/tofaktor.spec.ts måler layouten i en ekte nettleser.

   for leseren: alt i denne kommentaren er til utvikleren. Brukerteksten er den
   konseptsiden viser — og der konseptet ikke tegner tilstanden, den porten gir
   (src/auth/supabase.ts eier feilsetningene; leverandørens egne vises aldri).

   DEN FORKLARTE LÅSEN (K8, ux-flow-architect «Forklarte låser»): en aal1-sesjon får
   ikke panelet, og skjermen sier hva som åpner det — på skjermen, aldri som en stum
   403. To modus:
     oppsett     — ingen autentiseringsapp ennå. QR-en er HELTEN.
     utfordring  — appen finnes; FELTET er helten. Ingen forklaring: den som er her
                   har alt satt opp appen og lest hvorfor.

   DD-REFERANSER — én linje per DD:
   DD-08  Farge alene bærer aldri mening: feilen har ikon + tekst i tillegg til rød ramme.
   DD-11  Ingen <kbd> her — Enter på submit er innebygd, og <kbd> hører til
          aria-keyshortcuts på en handling. Esc gjør ingenting: ingen dialog finnes.
   DD-48  Merke + ordmerke som ÉN enhet øverst, «Adminpanel» i small under. Samme hode
          som innloggingen, som er uendret i R6.2.
   DD-51  Én breakpoint, målt i JS. Tofaktor rendres utenfor skallet, så målingen er
          løftet til App (P4) og kommer hit som `useSmal()` — aldri en @media med tall.
   DD-53  Rødt er feilens farge her, ikke en handlings: ingen knapp er rød.
   DD-58  Ett primærvalg: «Fullfør oppsettet» / «Bekreft». «Kan ikke skanne?»,
          «Åpne i autentiseringsappen», «Vis QR-koden», «Prøv igjen» og «Logg ut» er
          KNAPPER med lenkeutseende — aldri <a>, fordi ingen av dem er en navigasjon i
          appen. Ingen festet fot: kortet er kortere enn skjermen.
   DD-60  Ordene: «Skann koden», «Skriv inn nøkkelen i appen», «Kode fra appen»,
          «Fullfør oppsettet», «Bekreft», «Logg ut». «sakene», aldri «innspill» (panelets
          ord). K8-setningen sier ikke «8 timer» — sesjonsvakten sier det når det gjelder.
   axe    Seksjonen er merket av h1 i begge modus, som innloggingens kort. I utfordringen
          er h1 og feltets etikett SAMME ord (konseptets skjerm 3), så navnet finnes både
          på seksjonen og på feltet; feltet har likevel bare ÉN etikett (avvik 4), og
          testene spør etter det med `selector: 'input'`.
   DD-67  Beslutningsgrunnlaget i body: feltets etikett og K8-setningen i body, aldri small.
          Én helt per skjerm — i oppsettet QR-en (feltet i body), i utfordringen feltet
          (sifrene i h1-størrelse).
   DD-70  Feilens form er rød ramme på feltet (aria-invalid) + melding ved feltet, ikke en
          melding under knappene.
   K8     Setningen om hvorfor står ÉN gang i hele tofaktorflyten: i oppsettet, over
          QR-visningen og nøkkelvisningen. Utfordringen har ingen. (Innloggingens egen
          linje står — den varsler før noe er satt opp, P2.)

   TILSTANDENE (ux-flow-architect: alle defineres før UI-koden):
     laster        — `startOppsett` står ute: «Lager oppsettet …» som role="status", og en
                     plassholder i QR-ens mål så kortet ikke hopper når koden kommer.
                     Feltet og knappen er av.
     suksess       — QR-en (eller nøkkelen) står; feltet tar imot.
     opptatt       — koden verifiseres: feltet og den fylte knappen er av, teksten står.
     feil (kode)   — porten sier «Koden stemmer ikke …». Meldingen står ved feltet med
                     ikon, feltet får aria-invalid, og fokus går TILBAKE til feltet med
                     hele koden markert: neste kode erstatter den med ett tastetrykk, og
                     den gamle er uansett ubrukelig (TOTP roterer hvert 30. sekund).
                     Det som er skrevet slettes aldri av skjermen.
     feil (start)  — `startOppsett` feilet: meldingen som role="alert" og «Prøv igjen»,
                     som starter oppsettet på nytt. Ingen QR, intet felt å fylle.
     ingen app     — «Fant ingen autentiseringsapp på kontoen.» fra porten i utfordringen.
                     Veien videre er «Logg ut», som alltid står på skjermen; panelet har
                     ingen vei for en mistet telefon i v1 (Supabase-admin avregistrerer).
     utløpt sesjon — uendret: 401 håndteres av src/api/Sesjonsvakt.tsx, som logger ut med
                     melding på innloggingssiden. Ingenting her.
     under 900 px  — kortet er fullbredde uten ramme, og alle trykkflater 48 (klassen
                     panel-smal, som `.panel-smal button` alt bærer).
     mørk palett   — QR-flaten er HVIT uansett palett (tokenet screen.qrBackdrop):
                     en mørk QR-kode er uskannbar.

   TASTATURFLYTEN:
   | Tast  | Hva                                                      |
   |-------|----------------------------------------------------------|
   | Enter | sender skjemaet (innebygd i submit — uten hint, DD-11)   |
   | Tab   | felt → primærknapp → tekstknappene, i DOM-rekkefølge     |
   | Esc   | ingenting: skjermen har ingen dialog og ingen meny        |
   I utfordringen har feltet fokus fra start — skjermen har én oppgave.

   ÉN `TotpOppsett`, STARTET ÉN GANG PER MONTERING: `startOppsett` avregistrerer en
   uverifisert faktor og lager en NY hemmelighet (supabase.ts). QR og nøkkel er to
   visninger av det SAMME oppsettet, så «Kan ikke skanne?» og «Vis QR-koden» bytter bare
   `visning` — løftet holdes i en ref, og StrictMode (som kjører effekten to ganger)
   gjenbruker det i stedet for å kalle porten igjen.

   OPPSETTET AV APPEN BOR I `src/auth/Appoppsett.tsx`, ikke her. Det samme oppsettet står bak
   låsen også, i raden «Koden fra appen» på «Min innlogging», og to kopier av QR-en, nøkkelen,
   `otpauth://`-lenken og feltet ville vært to steder å endre ordene. Delt derfra:
   `useAppoppsett` (én hemmelighet per montering), `Oppsettshelt`, `Kodefelt`,
   `Visningsknapp` og `appoppsettTittel`. Formen rundt — kortet, hodet, K8-setningen,
   sportekstknappene og «Logg ut» — er denne skjermens, og blir her.

   NØKKELEN KOMMER ALDRI I UTKLIPPSTAVLEN (P3 a): den er den VARIGE hemmeligheten, og en
   utklippstavle med historikk og synkronisering beholder den. Ingen «Kopier» finnes, og
   utklippstavle-API-et nevnes ikke i denne filen eller i `Appoppsett.tsx` i det hele tatt —
   vakt 12 i test/lint-vakter.test.ts leser BEGGE filene som tekst og blir rød på navnet.
   Grupperingen i fire er CSS: DOM-teksten er den ugrupperte strengen.

   BERØRINGSENHET (P3 a): der pekeren er grov er autentiseringsappen på SAMME enhet som
   QR-en, og koden kan ikke skannes. Alternativet er da «Åpne i autentiseringsappen» —
   `otpauth://`-lenken oppsettet alt har — i stedet for «Kan ikke skanne?».

   ── ENHETEN SOM ANDRE FAKTOR (WebAuthn) ──────────────────────────────────────

   Skjermene har TO SPOR, ikke to nye skjermer: «enhet» (Windows Hello, Touch ID,
   sikkerhetsnøkkel) og «app» (dagens TOTP). Formen er den samme i begge — samme hode,
   samme h1-plass, samme ene fylte knapp med samme ord («Fullfør oppsettet» i oppsettet,
   «Bekreft» i utfordringen), samme tekstknapper under. Det eneste som byttes er HELTEN:
   QR-en eller feltet i appsporet, setningen og knappen i enhetssporet. Enheten har
   verken QR eller kode å vise; å finne på en grafikk for den ville vært pynt.

   HVILKET SPOR, OG HVORFOR:
     oppsett     — enhet når nettleseren kan det, ellers app. Enheten er sterkere
                   (phishing-resistent) og raskere, så den er primærvalget; appen står
                   bak «Bruk en autentiseringsapp i stedet», og veien tilbake er
                   «Bruk denne enheten i stedet». TOTP-oppsettet startes FØRST når
                   appsporet velges — `startOppsett` lager en ny hemmelighet hver gang,
                   og en hemmelighet ingen ba om er en hemmelighet for mye.
     utfordring  — den faktoren kontoen FAKTISK har, lest med `hentFaktorer()`. Har hun
                   begge: enheten først, «Bruk koden fra appen i stedet» som reserve.
                   Har hun bare én: den ene, uten et valg som ikke fører noe sted.
                   Har hun ingen: meldingen og «Logg ut» — aldri en død knapp.

   TOTP FORSVINNER IKKE. En bruker som satte opp appen i går skal ikke møte en skjerm
   som krever noe annet i dag, og enheten finnes ikke i alle nettlesere. Begge typer kan
   ligge på samme konto samtidig; porten sier hvilke.

   NETTLESEREN SPØRRES FØR DET TEGNES: `auth.stoetterEnhet()` er synkron, og svaret er
   nei i jsdom (ingen `navigator.credentials`) og i en usikker kontekst. Da er disse
   skjermene ORD FOR ORD som før enhetsfaktoren kom til — det er derfor
   `r62-tofaktor.test.tsx` og `tofaktor.test.tsx` er uendret i forventning. Enhetssporet
   måles av `test/webauthn-tofaktor.test.tsx`, og den EKTE seremonien av
   `e2e/webauthn.spec.ts` med Chromes virtuelle autentikator.

   OG UTRULLINGEN SPØRRES I TILLEGG: `useEnhet()` (src/auth/enhet.ts) er nettleserens
   svar OG konfigflagget `VITE_INNSPEL_DEVICE_FACTOR`, som er AV. Supabase støtter ikke
   faktoren ennå — Management API-et svarer «Enabling of MFA with WebAuthn not currently
   supported» (2026-09-21, docs/drift/supabase-enhetsfaktor.md) — og uten flagget ville
   «Bruk denne enheten» stått i enhver moderne nettleser og blitt avvist av serveren hver
   gang. I produksjon er derfor appsporet under det eneste som tegnes, akkurat som før.

   K8 ER IKKE MYKNET: begge spor ender i at PORTEN gir aal2, og skjermen spør om
   sesjonen på nytt etterpå. En registrert enhet gir ingen tilgang i seg selv.
------------------------------------------------------------------------------ */

/** Enhetssporets forklaring. Står ÉN gang, i oppsettet — utfordringen forklarer ingenting (K8). */
const K8_ENHET = 'Virksomheten din krever to steg, fordi panelet gir tilgang til personopplysninger. Enheten bekrefter med fingeravtrykk, ansikt eller PIN.';

/**
 * Setningen når panelet ikke fikk lest om leietakeren krever tofaktor. Den står i ALLE
 * varianter av skjermen, også de som ellers forklarer ingenting (utfordringen), fordi den
 * ikke forklarer KRAVET — den forklarer hvorfor skjermen er her når kravet er ukjent.
 * Uten den ser en server som er nede ut som et valg leietakeren har tatt.
 */
const UKJENT_KRAV = 'Vi fikk ikke kontakt med serveren, så vi vet ikke om tofaktor kreves her — og da spør vi. Last siden på nytt om litt.';

export function Tofaktor({ modus, ukjentKrav }: { modus: 'oppsett' | 'utfordring'; ukjentKrav?: boolean }) {
  const auth = useAuth();
  const smal = useSmal();
  useSidetittel('Logg inn');
  /**
   * Kan enheten brukes her? Nettleseren OG utrullingen må si ja, og begge leses ÉN gang
   * (`auth/enhet.ts`). Er svaret nei — som det er i produksjon til Supabase åpner for
   * faktoren, og i jsdom — er skjermene under ord for ord dagens.
   */
  const { mulig: enhetMulig } = useEnhet();
  /**
   * Hvilke faktorer kontoen har. I oppsettet har den ingen (det er derfor skjermen vises),
   * og uten støtte for enheten finnes bare appen — begge kjent uten å spørre, så
   * utfordringen slipper et laster-steg der den aldri hadde et.
   */
  const [faktorer, setFaktorer] = useState<Faktorer | null>(() => (modus === 'oppsett' ? { app: false, enhet: false, liste: [] } : enhetMulig ? null : { app: true, enhet: false, liste: [] }));
  const [spor, setSpor] = useState<'enhet' | 'app'>(() => (modus === 'oppsett' && enhetMulig ? 'enhet' : 'app'));
  /* Hemmeligheten lages først når appsporet i OPPSETTET er valgt — ikke før. */
  const { oppsett, startfeil, visning, setVisning, paaNytt } = useAppoppsett(modus === 'oppsett' && spor === 'app');
  const [kode, setKode] = useState('');
  const [feil, setFeil] = useState<string | null>(null);
  const [opptatt, setOpptatt] = useState(false);
  const feltet = useRef<HTMLInputElement>(null);
  const faktorløftet = useRef<Promise<Faktorer> | null>(null);
  const refokus = useRef(false);

  /**
   * Utfordringen spør hvilke faktorer kontoen har, én gang per montering. Løftet holdes i
   * en ref av samme grunn som `startOppsett`: StrictMode kjører effekten to ganger, og to
   * oppslag ville gitt to svar der ett holder.
   */
  useEffect(() => {
    if (modus !== 'utfordring' || !enhetMulig) return undefined;
    const løfte = (faktorløftet.current ??= auth.hentFaktorer());
    let aktiv = true;
    løfte
      .then((f) => {
        if (!aktiv) return;
        setFaktorer(f);
        setSpor(f.enhet ? 'enhet' : 'app');
      })
      .catch(() => {
        // Klarer vi ikke lese faktorene, er appen den trygge antakelsen: dét sporet
        // virker for alle som har den, og «Logg ut» står uansett.
        if (aktiv) setFaktorer({ app: true, enhet: false, liste: [] });
      });
    return () => {
      aktiv = false;
    };
  }, [auth, modus, enhetMulig]);

  /**
   * Fokus tilbake til feltet etter en avvist kode — i en effekt, ikke i `catch`, fordi
   * feltet er AV mens koden verifiseres og `focus()` på et deaktivert felt gjør
   * ingenting. Effekten kjører når `opptatt` er falt tilbake og feltet er levende igjen.
   */
  useEffect(() => {
    if (!refokus.current || opptatt) return;
    refokus.current = false;
    feltet.current?.focus();
    feltet.current?.select();
  }, [feil, opptatt]);

  const bekreft = (e: FormEvent) => {
    e.preventDefault();
    setFeil(null);
    setOpptatt(true);
    const løfte = modus === 'oppsett' && oppsett ? auth.bekreftOppsett(oppsett.faktorId, kode) : auth.bekreftUtfordring(kode);
    løfte
      .catch((err: unknown) => {
        setFeil(err instanceof Error ? err.message : 'Koden ble ikke godtatt.');
        refokus.current = true;
      })
      .finally(() => setOpptatt(false));
  };

  /**
   * Enhetssporets ene handling. Seremonien eies av porten; her er bare tilstanden rundt
   * den. Ingen refokus: det finnes ikke noe felt å rette, og knappen står igjen slik den
   * var — et nytt forsøk er ett trykk unna.
   */
  const bekreftMedEnhet = (e: FormEvent) => {
    e.preventDefault();
    setFeil(null);
    setOpptatt(true);
    const løfte = modus === 'oppsett' ? auth.settOppEnhet() : auth.bekreftMedEnhet();
    løfte
      // KUN `AuthFeil`, som er panelets egne setninger. En `DOMException` fra
      // nettleseren («This is an invalid domain.») er maskinens ord og forteller
      // brukeren ingenting hun kan gjøre noe med — den blir panelets setning i stedet.
      .catch((err: unknown) => setFeil(err instanceof AuthFeil ? err.message : 'Enheten bekreftet ikke. Prøv igjen.'))
      .finally(() => setOpptatt(false));
  };

  /** Bytter spor og legger fra seg feilen: den gjaldt veien brukeren nettopp forlot. */
  const byttSpor = (til: 'enhet' | 'app') => {
    setFeil(null);
    setKode('');
    setSpor(til);
  };

  const handling = modus === 'oppsett' ? 'Fullfør oppsettet' : 'Bekreft';
  /**
   * Står rett under h1 i ALLE varianter, også i utfordringen som ellers forklarer
   * ingenting: dette er ikke K8-setningen, det er en unntakstilstand som sier hvorfor
   * skjermen er her når kravet ikke kunne leses. Den kommer derfor FØR helten i oppsettet,
   * som `startfeil` gjør — R6.2s «≤ 20 ord før QR-koden» måles på normaltilstanden.
   */
  const ukjent = ukjentKrav ? (
    <p role="status" className="panel-tekst" data-testid="ukjent-krav">
      <Ikon navn="warning-outline" /> {UKJENT_KRAV}
    </p>
  ) : null;
  const loggUt = (
    <button type="button" className="panel-knapp-lenke" onClick={() => void auth.loggUt()} disabled={opptatt}>
      <Ikon navn="log-out-outline" />
      Logg ut
    </button>
  );

  /* Utfordringen venter på svaret om hvilke faktorer kontoen har. Kort, og bare der
     enheten i det hele tatt er mulig — ellers hadde svaret vært kjent ved første render. */
  if (!faktorer) {
    return (
      <main className={smal ? 'panel-enkelt panel-smal' : 'panel-enkelt'} id="innhold">
        <section className="panel-kort panel-kort-midt" aria-labelledby="tofaktor-h">
          <Merkehode storrelse="xl">
            <div className="panel-merke-under">Adminpanel</div>
          </Merkehode>
          <h1 id="tofaktor-h" className="panel-h1">
            Logg inn
          </h1>
          {ukjent}
          <p role="status" className="panel-tekst">
            Henter måtene du kan bekrefte på …
          </p>
          <div className="panel-lenker">{loggUt}</div>
        </section>
      </main>
    );
  }

  /* Utfordring uten en eneste faktor: leverandøren lovet aal2, men faktoren er borte —
     avregistrert av en administrator, eller mistet. Panelet har ingen gjenopprettingsvei
     i v1, så det ærlige svaret er meldingen og veien ut. Aldri en knapp som ikke kan
     virke. */
  if (modus === 'utfordring' && !faktorer.app && !faktorer.enhet) {
    return (
      <main className={smal ? 'panel-enkelt panel-smal' : 'panel-enkelt'} id="innhold">
        <section className="panel-kort panel-kort-midt" aria-labelledby="tofaktor-h">
          <Merkehode storrelse="xl">
            <div className="panel-merke-under">Adminpanel</div>
          </Merkehode>
          <h1 id="tofaktor-h" className="panel-h1">
            Bekreft at det er deg
          </h1>
          {ukjent}
          <p role="alert" className="panel-feltfeil">
            <Ikon navn="alert-circle-outline" />
            Fant verken en autentiseringsapp eller en enhet på kontoen.
          </p>
          <div className="panel-lenker">{loggUt}</div>
        </section>
      </main>
    );
  }

  /* ENHETSSPORET. Samme kort, samme knappeord — helten er setningen og knappen. */
  if (spor === 'enhet') {
    /* Reserven vises bare når den finnes: i oppsettet kan appen alltid velges, i
       utfordringen bare av den som faktisk HAR den. */
    const reserve = modus === 'oppsett' ? 'Bruk en autentiseringsapp i stedet' : faktorer.app ? 'Bruk koden fra appen i stedet' : null;
    return (
      <main className={smal ? 'panel-enkelt panel-smal' : 'panel-enkelt'} id="innhold">
        <section className="panel-kort panel-kort-midt" aria-labelledby="tofaktor-h">
          <Merkehode storrelse="xl">
            <div className="panel-merke-under">Adminpanel</div>
          </Merkehode>
          <h1 id="tofaktor-h" className="panel-h1">
            Bruk denne enheten
          </h1>
          {ukjent}

          <form onSubmit={bekreftMedEnhet} className="panel-skjema">
            {/* Setningen om hvorfor: ÉN gang i flyten, og bare i oppsettet (K8, P2). */}
            {modus === 'oppsett' ? <p className="panel-tekst">{K8_ENHET}</p> : null}

            <button type="submit" className="panel-knapp" disabled={opptatt}>
              {handling}
            </button>

            {/* Seremonien er nettleserens egen dialog; skjermen sier bare at den venter. */}
            {opptatt ? (
              <p role="status" className="panel-tekst">
                Venter på enheten …
              </p>
            ) : null}

            {feil ? (
              <p role="alert" className="panel-feltfeil">
                <Ikon navn="alert-circle-outline" />
                {feil}
              </p>
            ) : null}

            <div className="panel-lenker">
              {reserve ? (
                <button type="button" className="panel-knapp-lenke" onClick={() => byttSpor('app')} disabled={opptatt}>
                  {reserve}
                </button>
              ) : null}
              {loggUt}
            </div>
          </form>
        </section>
      </main>
    );
  }

  /** Feltet og den fylte knappen er av mens oppsettet lages og mens koden verifiseres. */
  const av = opptatt || (modus === 'oppsett' && !oppsett);
  const tittel = modus === 'utfordring' ? 'Kode fra appen' : appoppsettTittel(visning);
  /* Veien tilbake til enheten står bare der enheten er mulig — og i utfordringen bare
     for den som har registrert en. */
  const tilbakeTilEnhet = enhetMulig && (modus === 'oppsett' || faktorer.enhet);

  return (
    <main className={smal ? 'panel-enkelt panel-smal' : 'panel-enkelt'} id="innhold">
      <section className="panel-kort panel-kort-midt" aria-labelledby="tofaktor-h">
        <Merkehode storrelse="xl">
          <div className="panel-merke-under">Adminpanel</div>
        </Merkehode>
        <h1 id="tofaktor-h" className="panel-h1">
          {tittel}
        </h1>
        {ukjent}

        {/* Helten, før alt annet: fire ord står over QR-koden (kriteriet er ≤ 20). */}
        {modus === 'oppsett' && !startfeil ? <Oppsettshelt oppsett={oppsett} visning={visning} /> : null}

        {startfeil ? (
          <>
            <p role="alert" className="panel-feltfeil">
              <Ikon navn="alert-circle-outline" />
              {startfeil}
            </p>
            <div className="panel-lenker">
              <button type="button" className="panel-knapp-lenke" onClick={paaNytt}>
                Prøv igjen
              </button>
              {loggUt}
            </div>
          </>
        ) : (
          <form onSubmit={bekreft} className="panel-skjema">
            {/* Etiketten finnes for skjermleseren i BEGGE modus (avvik 4): h1 er seksjonens
                navn, og to kilder til samme navn ville gitt feltet to etiketter. I oppsettet
                bærer en dekorativ linje den for øyet — samme mønster som «Kopier» i
                programsiden — fordi h1 der er «Skann koden». */}
            <Kodefelt feltRef={feltet} kode={kode} onKode={setKode} feil={feil} av={av} helt={modus === 'utfordring'} synligEtikett={modus === 'oppsett'} autoFokus={modus === 'utfordring'} />

            <button type="submit" className="panel-knapp" disabled={av}>
              {modus === 'oppsett' ? 'Fullfør oppsettet' : 'Bekreft'}
            </button>

            {/* Setningen om hvorfor: ÉN gang i hele flyten, og bare i oppsettet (K8, P2). */}
            {modus === 'oppsett' && visning === 'qr' ? <p className="panel-tekst">Virksomheten din krever en autentiseringsapp, fordi panelet gir tilgang til personopplysninger.</p> : null}

            <div className="panel-lenker">
              {modus === 'oppsett' ? <Visningsknapp oppsett={oppsett} visning={visning} onVisning={setVisning} /> : null}
              {tilbakeTilEnhet ? (
                <button type="button" className="panel-knapp-lenke" onClick={() => byttSpor('enhet')} disabled={opptatt}>
                  Bruk denne enheten i stedet
                </button>
              ) : null}
              {loggUt}
            </div>
          </form>
        )}
      </section>
    </main>
  );
}
